import { zodResolver } from '@hookform/resolvers/zod';
import { authErrorMessage, PASSWORD_MIN_LENGTH, ResetPasswordFormInput } from '@myhome/contracts';
import { Button } from '@myhome/ui/components/button';
import { Form } from '@myhome/ui/components/form';
import { SpinnerGap } from '@myhome/ui/icons';
import { StatusBanner } from '@myhome/ui/patterns/status-banner';
import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { authClient } from '../lib/auth-client';
import { FormError, PasswordField } from './auth-screen';

/**
 * The API checks the emailed token first and sends the browser on with
 * `?token=…`, or with `?error=INVALID_TOKEN` when it is used or expired.
 */
export const TokenSearch = z.object({
  token: z.string().min(1).optional().catch(undefined),
});

/**
 * Choosing a password from an emailed link: a reset, or accepting an admin
 * invitation, which is a reset by another name (auth.md, Admin invite).
 */
export function NewPasswordForm({
  token,
  submitLabel,
  onDone,
}: {
  token: string | undefined;
  submitLabel: string;
  onDone: 'password-set' | 'password-reset';
}) {
  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(ResetPasswordFormInput),
    defaultValues: { newPassword: '', confirmPassword: '' },
    mode: 'onBlur',
  });

  if (token === undefined) {
    return (
      <StatusBanner
        tone="warning"
        title="This link is not valid any more"
        description={
          onDone === 'password-set'
            ? 'Invitation links last 1 hour and work once. Ask the admin who invited you to send a new one.'
            : 'Reset links last 1 hour and work once. Ask for a new one and use the latest email.'
        }
        action={
          onDone === 'password-reset' ? (
            <Button asChild variant="outline" size="sm">
              <Link to="/forgot-password">Send me a new link</Link>
            </Button>
          ) : undefined
        }
      />
    );
  }

  const onSubmit = form.handleSubmit(async ({ newPassword }) => {
    const { error } = await authClient.resetPassword({ newPassword, token });
    if (error !== null) {
      form.setError('root', { message: authErrorMessage(error) });
      return;
    }
    await navigate({ to: '/login', search: { notice: onDone }, replace: true });
  });

  const { isSubmitting, errors } = form.formState;

  return (
    <Form {...form}>
      <form noValidate onSubmit={onSubmit} className="flex flex-col gap-3">
        <FormError message={errors.root?.message} />
        <PasswordField
          control={form.control}
          name="newPassword"
          label="Password"
          autoComplete="new-password"
          description={`At least ${PASSWORD_MIN_LENGTH} characters. A short sentence is easy to remember.`}
        />
        <PasswordField
          control={form.control}
          name="confirmPassword"
          label="Type it again"
          autoComplete="new-password"
        />
        <Button type="submit" block disabled={isSubmitting}>
          {isSubmitting ? (
            <SpinnerGap className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : null}
          {submitLabel}
        </Button>
      </form>
    </Form>
  );
}
